import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Column,
  FormGroup,
  Button,
  TextInput,
  TextArea,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  FileUploader,
} from "carbon-components-react";
import { Edit32, TrashCan32 } from "@carbon/icons-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import http from "../../http-common";
import { toast } from "react-toastify";
import * as yup from "yup";
import { getDocumentTypeDescription } from "../../services/Utils";

const teamMemberSchema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  role: yup.string().required("Função é obrigatória"),
  activities: yup.string().required("Atividades são obrigatórias"),
  documents: yup
    .array()
    .of(
      yup.object().shape({
        fileName: yup.string().required(),
        fileContent: yup.string().required(),
        fileType: yup.string().required(),
        documentType: yup.string().required(),
        size: yup.number().required(),
      })
    )
    .test("required-documents", "Documentos obrigatórios em falta", (docs) => {
      if (!docs) return false;
      return (
        docs.some((doc) => doc.documentType === "MEMBER_CV") &&
        docs.some((doc) => doc.documentType === "MEMBER_CERTIFICATIONS")
      );
    }),
});

const TeamForm = ({
  teamMembers,
  setTeamMembers,
  isUpdate = false,
  applicationUuid = null,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [fileStatus, setFileStatus] = useState({
    MEMBER_CV: "edit",
    MEMBER_CERTIFICATIONS: "edit",
  });

  const cvUploaderRef = useRef(null);
  const certUploaderRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(teamMemberSchema),
    defaultValues: {
      name: "",
      role: "",
      activities: "",
      documents: [],
    },
  });

  useEffect(() => {
    if (isUpdate && applicationUuid) {
      fetchTeamMembers();
    }
  }, [isUpdate, applicationUuid]);

  const fetchTeamMembers = async () => {
    try {
      const response = await http.get(
        `/applications/${applicationUuid}/team-members`
      );
      setTeamMembers(response.data);
    } catch (error) {
      // toast.error("Erro ao carregar membros da equipe");
    }
  };

  const processFile = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      throw new Error("Arquivo excede o limite de 10MB");
    }

    if (!file.type.includes("pdf")) {
      throw new Error("Apenas arquivos PDF são permitidos");
    }

    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (documentType, files) => {
    const file = files?.[0];
    if (!file) return;

    try {
      const fileContent = await processFile(file);
      const document = {
        fileName: file.name,
        fileContent: fileContent,
        fileType: file.type,
        documentType: documentType,
        size: file.size,
      };

      setFileStatus((prev) => ({ ...prev, [documentType]: "complete" }));

      const currentDocs = getValues("documents") || [];
      setValue(
        "documents",
        [
          ...currentDocs.filter((doc) => doc.documentType !== documentType),
          document,
        ],
        { shouldValidate: true }
      );

      // toast.success("Documento carregado com sucesso");
    } catch (error) {
      toast.error(error.message);
      setFileStatus((prev) => ({ ...prev, [documentType]: "edit" }));
    }
  };

  const handleMemberSubmit = async (data) => {
    try {
      const memberData = {
        name: data.name,
        role: data.role,
        activities: data.activities,
        documents: data.documents,
      };

      if (editingId) {
        // If editing, include the existing ID
        memberData.id = editingId;
        setTeamMembers((prev) =>
          prev.map((member) => (member.id === editingId ? memberData : member))
        );
        toast.success("Membro atualizado com sucesso");
      } else {
        // If creating new, don't include an ID
        setTeamMembers((prev) => [...prev, memberData]);
        toast.success("Membro adicionado com sucesso");
      }
      clearForm();
    } catch (error) {
      toast.error("Erro ao salvar membro");
    }
  };

  const clearForm = () => {
    reset({
      name: "",
      role: "",
      activities: "",
      documents: [],
    });
    setEditingId(null);
    setFileStatus({
      MEMBER_CV: "edit",
      MEMBER_CERTIFICATIONS: "edit",
    });

    // Clear file uploaders
    if (cvUploaderRef.current) {
      cvUploaderRef.current.value = "";
    }
    if (certUploaderRef.current) {
      certUploaderRef.current.value = "";
    }
  };

  const handleEdit = (member) => {
    setEditingId(member.id);
    reset({
      name: member.name,
      role: member.role,
      activities: member.activities,
      documents: member.documents || [],
    });

    const newFileStatus = {
      MEMBER_CV: "edit",
      MEMBER_CERTIFICATIONS: "edit",
    };
    member.documents?.forEach((doc) => {
      newFileStatus[doc.documentType] = "complete";
    });
    setFileStatus(newFileStatus);
  };

  const handleRemove = async (memberId) => {
    try {
      if (isUpdate) {
        await http.delete(`/applications/team-members/${memberId}`);
      }
      setTeamMembers((prev) => prev.filter((m) => m.id !== memberId));
      if (editingId === memberId) {
        clearForm();
      }
      toast.success("Membro removido com sucesso");
    } catch (error) {
      toast.error("Erro ao remover membro");
    }
  };

  return (
    <FormGroup
      legendText="EQUIPA DE DESENVOLVIMENTO DA PLATAFORMA"
      className="section-header"
    >
      <fieldset className="bx--fieldset">
        <div className="border rounded-lg p-4 mb-4">
          <Row className="mb-4">
            <Column lg={6} md={6} sm={12}>
              <TextInput
                id="name"
                name="name"
                labelText="9.1 Nome Completo"
                invalid={!!errors.name}
                invalidText={errors.name?.message}
                {...register("name")}
              />
            </Column>
            <Column lg={6} md={6} sm={12}>
              <TextInput
                id="role"
                name="role"
                labelText="9.2. Função"
                invalid={!!errors.role}
                invalidText={errors.role?.message}
                {...register("role")}
              />
            </Column>
          </Row>
          <Row className="mb-4">
            <Column lg={12} md={12} sm={12}>
              <TextArea
                id="activities"
                name="activities"
                labelText="9.3. Actividades"
                invalid={!!errors.activities}
                invalidText={errors.activities?.message}
                {...register("activities")}
              />
            </Column>
          </Row>
          <Row className="mb-4">
            <Column lg={6} md={6} sm={12}>
              <FileUploader
                ref={cvUploaderRef}
                accept={[".pdf"]}
                buttonLabel="Carregar Arquivo"
                filenameStatus={fileStatus.MEMBER_CV}
                labelTitle="9.4. Currículo"
                labelDescription="Anexar CV do membro (formato PDF)"
                onChange={(e) => handleFileChange("MEMBER_CV", e.target.files)}
              />
            </Column>
            <Column lg={6} md={6} sm={12}>
              <FileUploader
                ref={certUploaderRef}
                accept={[".pdf"]}
                buttonLabel="Carregar Arquivo"
                filenameStatus={fileStatus.MEMBER_CERTIFICATIONS}
                labelTitle="9.5. Certificações"
                labelDescription="Anexar certificações do membro (formato PDF)"
                onChange={(e) =>
                  handleFileChange("MEMBER_CERTIFICATIONS", e.target.files)
                }
              />
            </Column>
          </Row>
          <Row className="mt-2 mb-4">
            <Column lg={12} md={12} sm={12}>
              <div className="form-buttons">
                <p></p>
                <Button
                  onClick={handleSubmit(handleMemberSubmit)}
                  type="button"
                >
                  {editingId ? "Atualizar" : "Adicionar"}
                </Button>
                {editingId && (
                  <Button
                    kind="secondary"
                    onClick={clearForm}
                    className="ml-2"
                    type="button"
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </Column>
          </Row>
        </div>
        <hr className="mt-2"></hr>
        {teamMembers.length > 0 && (
          <DataTable
            rows={teamMembers}
            headers={[
              { key: "name", header: "Nome" },
              { key: "role", header: "Função" },
              { key: "activities", header: "Atividades" },
              { key: "documents", header: "Documentos" },
              { key: "actions", header: "Ações" },
            ]}
          >
            {({ rows, headers, getHeaderProps, getTableProps }) => (
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => (
                      <TableHeader
                        key={header.key}
                        {...getHeaderProps({ header })}
                      >
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {teamMembers.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.role}</TableCell>
                      <TableCell>{member.activities}</TableCell>
                      <TableCell>
                        {member.documents?.map((doc) => (
                          <div key={doc.documentType}>
                            {getDocumentTypeDescription(doc.documentType)}:{" "}
                            {doc.fileName}
                          </div>
                        )) || "Nenhum documento anexado"}
                      </TableCell>
                      <TableCell>
                        <Button
                          kind="ghost"
                          size="small"
                          renderIcon={Edit32}
                          iconDescription="Editar"
                          onClick={() => handleEdit(member)}
                          type="button"
                        />
                        <Button
                          kind="danger--ghost"
                          size="small"
                          renderIcon={TrashCan32}
                          iconDescription="Remover"
                          onClick={() => handleRemove(member.id)}
                          type="button"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DataTable>
        )}
      </fieldset>
    </FormGroup>
  );
};

export default TeamForm;
